import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader } from "rsuite";
import Sidebar from "../../components/Sidebar/Sidebar";
import { Topbar } from "../../components/Topbar/Topbar";
import StudentActivityChart from "../../components/StudentActivityChart/StudentActivityChart";
import AccuracyChart from "../../components/AccuracyChart/AccuracyChart";
import AllTimeStatsSection from "../../components/AllTimeStatsSection/AllTimeStatsSection";
import StudyHabitsSection from "../../components/StudyHabitsSection/StudyHabitsSection";
import TopicBreakdownSection from "../../components/TopicBreakdownSection/TopicBreakdownSection";
import SplitToggle, {
  CHART_PERIOD_OPTIONS,
  ChartPeriod,
} from "../../components/SplitToggle";
import { useUser } from "../../context/UserContext";
import axiosClient from "../../axiosClient";
import { shouldShowErrorToast } from "../../lib/accessExpired";
import { Module } from "../../types";
import { TopicStat, UserStatsResponse, EMPTY_PLATFORM_SUMMARY, platformSummaryToStreakStats } from "../../constants/dashboardStats";
import "./StatsPage.scss";

const moduleToTopicStat = (module: Module): TopicStat => ({
  moduleId: module.id,
  name: module.name,
  score:
    module.numberOfQuestionsCompleted > 0
      ? Math.round(
          (module.correctlyAnsweredQuestionsCount / module.numberOfQuestionsCompleted) * 100
        )
      : 0,
  questionsDone: module.numberOfQuestionsCompleted,
});

const getPeriodKey = (period: ChartPeriod) => (period === 7 ? "last7Days" : "last30Days");

const StatsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const subjectIdParam = searchParams.get("subjectId");
  const { user, enrolments, fetchEnrolments, platformSummary } = useUser();
  const studentView = user?.type === "student";
  const [chartPeriod, setChartPeriod] = useState<ChartPeriod>(30);
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [userStats, setUserStats] = useState<UserStatsResponse | null>(null);
  const [isLoadingEnrolments, setIsLoadingEnrolments] = useState(studentView);
  const [isLoadingPage, setIsLoadingPage] = useState(false);

  const selectedEnrolment = useMemo(
    () => enrolments.find((enrolment) => enrolment.classId === selectedClassId),
    [enrolments, selectedClassId]
  );

  const moduleTopics = useMemo(
    () => modules.map(moduleToTopicStat),
    [modules]
  );

  const periodKey = getPeriodKey(chartPeriod);
  const chartData = userStats?.dailyActivity[periodKey] ?? [];
  const habits = userStats?.studyHabits[periodKey];

  const isPageReady =
    !isLoadingEnrolments &&
    !isLoadingPage &&
    selectedClassId !== null &&
    userStats !== null;

  useEffect(() => {
    if (!studentView) return;

    const loadEnrolments = async () => {
      setIsLoadingEnrolments(true);

      try {
        await fetchEnrolments();
      } catch (err) {
        if (shouldShowErrorToast(err)) {
          toast.error("Error loading question banks. Please seek support.");
        }
        console.error(err);
      } finally {
        setIsLoadingEnrolments(false);
      }
    };

    loadEnrolments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentView]);

  useEffect(() => {
    if (enrolments.length === 0) return;

    if (subjectIdParam) {
      const match = enrolments.find(
        (enrolment) => String(enrolment.subjectId) === subjectIdParam
      );
      if (match) {
        setSelectedClassId(match.classId);
        return;
      }
    }

    const fallback = enrolments[0];
    setSelectedClassId(fallback.classId);
    if (subjectIdParam !== String(fallback.subjectId)) {
      setSearchParams({ subjectId: String(fallback.subjectId) }, { replace: true });
    }
  }, [enrolments, subjectIdParam, setSearchParams]);

  const handleQuestionBankChange = (classId: number) => {
    setSelectedClassId(classId);

    const enrolment = enrolments.find((entry) => entry.classId === classId);
    if (enrolment) {
      setSearchParams({ subjectId: String(enrolment.subjectId) }, { replace: true });
    }
  };

  useEffect(() => {
    if (!selectedClassId || !selectedEnrolment) {
      setUserStats(null);
      setModules([]);
      return;
    }

    let cancelled = false;

    const loadPageData = async () => {
      setIsLoadingPage(true);
      setUserStats(null);
      setModules([]);

      try {
        const [statsResponse, modulesResponse] = await Promise.all([
          axiosClient.get<UserStatsResponse>(`/user-stats?classId=${selectedClassId}`),
          axiosClient.get<{ modules: Module[] }>(
            `/subjects/${selectedEnrolment.subjectId}/modules`
          ),
        ]);

        if (cancelled) return;

        const sortedModules = [...modulesResponse.data.modules]
          .filter((module) => module.numberOfQuestions > 0)
          .sort((a, b) =>
            a.name.localeCompare(b.name, undefined, {
              numeric: true,
              sensitivity: "base",
            })
          );

        setUserStats(statsResponse.data);
        setModules(sortedModules);
      } catch (err) {
        if (cancelled) return;

        if (shouldShowErrorToast(err)) {
          toast.error("Error loading statistics. Please seek support.");
        }
        console.error(err);
        setUserStats(null);
        setModules([]);
      } finally {
        if (!cancelled) {
          setIsLoadingPage(false);
        }
      }
    };

    loadPageData();

    return () => {
      cancelled = true;
    };
  }, [selectedClassId, selectedEnrolment]);

  const handlePracticeModule = (topic: TopicStat) => {
    if (!topic.moduleId || !selectedEnrolment) return;

    navigate("/practice", {
      state: {
        assignmentName: "Quick Practice",
        practiceModuleIds: [topic.moduleId],
        practiceSubjectId: selectedEnrolment.subjectId,
        examBoards: [],
      },
    });
  };

  const studentContent = (
    <>
      <div className="stats-intro-row">
        <p className="stats-subtitle">
          Track your progress, spot weak areas, and see how you compare with peers.
        </p>
        <div className="stats-course-select">
          <label htmlFor="courseSelect">Question bank</label>
          <select
            id="courseSelect"
            value={selectedClassId ?? ""}
            onChange={(e) => handleQuestionBankChange(Number(e.target.value))}
            disabled={enrolments.length === 0 || isLoadingPage}
          >
            {enrolments.length === 0 ? (
              <option value="">No question banks enrolled</option>
            ) : (
              enrolments.map((enrolment) => (
                <option key={enrolment.classId} value={enrolment.classId}>
                  {enrolment.className}
                </option>
              ))
            )}
          </select>
        </div>
      </div>

      {isLoadingEnrolments || isLoadingPage || (enrolments.length > 0 && !selectedClassId) ? (
        <div className="stats-page-loader">
          <Loader size="md" content="Loading statistics..." />
        </div>
      ) : isPageReady && userStats ? (
        <div className="stats-page-body stats-page-body--ready">
          <AllTimeStatsSection
            stats={userStats.allTime}
            heatmapDays={userStats.activityHeatmap.days}
            platformStreak={platformSummaryToStreakStats(
              platformSummary ?? EMPTY_PLATFORM_SUMMARY
            )}
          />

          <div className="stats-group-header stats-group-header--spaced">Recent activity</div>

          <section className="stats-section">
            <div className="stats-chart-header">
              <h3 className="stats-section-title">Questions completed (last {chartPeriod} days)</h3>
              <SplitToggle
                value={chartPeriod}
                onChange={setChartPeriod}
                options={CHART_PERIOD_OPTIONS}
                ariaLabel="Chart time period"
                id="chart"
              />
            </div>
            <div className="stats-chart-legend">
              <span className="stats-legend-item">
                <span className="stats-legend-dot stats-legend-dot--total" />
                Total
              </span>
              <span className="stats-legend-item">
                <span className="stats-legend-dot stats-legend-dot--correct" />
                Correct
              </span>
              <span className="stats-legend-item">
                <span className="stats-legend-dot stats-legend-dot--incorrect" />
                Incorrect
              </span>
            </div>
            <StudentActivityChart data={chartData} />
          </section>

          <section className="stats-section">
            <div className="stats-chart-header">
              <h3 className="stats-section-title">
                Average correctness over time (last {chartPeriod} days)
              </h3>
              <SplitToggle
                value={chartPeriod}
                onChange={setChartPeriod}
                options={CHART_PERIOD_OPTIONS}
                ariaLabel="Chart time period"
                id="accuracy"
              />
            </div>
            <AccuracyChart data={chartData} />
          </section>

          {habits ? (
            <StudyHabitsSection
              habits={habits}
              period={chartPeriod}
              onPeriodChange={setChartPeriod}
            />
          ) : null}

          <TopicBreakdownSection
            topics={moduleTopics}
            subjectId={selectedEnrolment?.subjectId}
            onPracticeTopic={handlePracticeModule}
          />
        </div>
      ) : null}
    </>
  );

  return (
    <div className="page-wrapper" id="StatsPage">
      <Sidebar />
      <div className="internal-page-wrapper">
        <Topbar title="Your Statistics" />
        <main>
          <div className="stats-page-content">
            {studentView ? (
              studentContent
            ) : (
              <p className="stats-subtitle">
                Teacher statistics will be available in a future update.
              </p>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default StatsPage;
